import { fmtCompact, fmtPct, fmtNum } from '../utils/fmt';
import { useState, useEffect, useCallback, useRef } from 'react';
import { fmtPrice, fngLabel } from '../utils/format';
import { fetchWithCache, getFetchMeta } from '../utils/fetchWithCache';
import InfoTooltip from './InfoTooltip';
import SourceDot, { SourceAttr, mergeMeta } from './SourceDot';
import DataSourceDown from './DataSourceDown';
import { fetchLinearTickers, tickersMeta } from '../utils/bybit';
import { openLanding } from '../utils/landing';
import { PLUSZ_PATH } from '../utils/pricing';
import './DerivativesTab.css';
import { openAlertDraft } from '../utils/alerts';

const BYBIT   = 'https://api.bybit.com/v5';
const BINANCE  = 'https://fapi.binance.com/fapi/v1';
const REFRESH_MS = 2 * 60 * 1000;
const CACHE_TTL  = 2 * 60 * 1000;

const ALL_COINS = [
  { sym: 'BTCUSDT',    label: 'BTC'    },
  { sym: 'ETHUSDT',    label: 'ETH'    },
  { sym: 'SOLUSDT',    label: 'SOL'    },
  { sym: 'BNBUSDT',    label: 'BNB'    },
  { sym: 'XRPUSDT',    label: 'XRP'    },
  { sym: 'DOGEUSDT',   label: 'DOGE'   },
  { sym: 'SUIUSDT',    label: 'SUI'    },
  { sym: 'HYPEUSDT',   label: 'HYPE'   },
  { sym: 'AVAXUSDT',   label: 'AVAX'   },
  { sym: 'LINKUSDT',   label: 'LINK'   },
  { sym: 'ADAUSDT',    label: 'ADA'    },
  { sym: 'DOTUSDT',    label: 'DOT'    },
  { sym: 'NEARUSDT',   label: 'NEAR'   },
  // TON: no Bybit linear perpetual (TONUSDT returns an empty list) — removed.
  // PEPE and BONK trade as 1000× contracts on Bybit.
  { sym: '1000PEPEUSDT', label: 'PEPE'  },
  { sym: 'WIFUSDT',    label: 'WIF'    },
  { sym: '1000BONKUSDT', label: 'BONK'  },
  { sym: 'ARBUSDT',    label: 'ARB'    },
  { sym: 'OPUSDT',     label: 'OP'     },
  { sym: 'INJUSDT',    label: 'INJ'    },
  { sym: 'TIAUSDT',    label: 'TIA'    },
  { sym: 'JUPUSDT',    label: 'JUP'    },
  { sym: 'RENDERUSDT', label: 'RENDER' },
];

const FREE_SYMS = new Set(['BTCUSDT', 'ETHUSDT']);

const PREMIUM_SYMS   = ['BTCUSDT','ETHUSDT','SOLUSDT','XRPUSDT','BNBUSDT','DOGEUSDT','HYPEUSDT','AVAXUSDT'];

const LIQ_LEVERAGES = [
  { lev: 2,  labelRisk: 'Alacsony kockázat' },
  { lev: 5,  labelRisk: 'Közepes kockázat'  },
  { lev: 10, labelRisk: 'Magas kockázat'    },
  { lev: 20, labelRisk: 'Nagyon magas'      },
  { lev: 50, labelRisk: 'Extrém kockázat'   },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/* Runs fn over items with at most `limit` requests in flight — the per-coin
   OI and long/short calls must not all start in the same millisecond. */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const i = next++;
      try { out[i] = { status: 'fulfilled', value: await fn(items[i]) }; }
      catch (reason) { out[i] = { status: 'rejected', reason }; }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

function fmtUSD(n) {
  if (n == null || isNaN(n)) return '—';
  return fmtCompact(n);
}

function fmtRate(r) {
  if (r == null) return '—';
  return fmtPct(r * 100, 4);
}

function fmtAPR(r) {
  if (r == null) return '—';
  return fmtPct(r * 3 * 365 * 100, 1);
}


function premiumState(pct) {
  if (pct == null)  return { text: '—',              color: 'var(--text3)', desc: '—' };
  if (pct >  0.1)   return { text: 'Contango',        color: '#00c853',     desc: 'Bullish: futures drágább mint spot' };
  if (pct < -0.1)   return { text: 'Backwardation',   color: '#ff4d4d',     desc: 'Bearish: futures olcsóbb mint spot' };
  return                   { text: 'Semleges',         color: 'var(--text3)', desc: 'Közel azonos ár' };
}

function rateColor(r) {
  if (r == null) return 'var(--text3)';
  const p = r * 100;
  if (p > 0.03)  return '#ff4d4d';
  if (p > 0.01)  return '#f5a623';
  if (p < -0.03) return '#00e5ff';
  if (p < -0.01) return '#00c853';
  if (p < 0)     return '#69f0ae';
  return 'var(--text2)';
}

function fundSignal(r) {
  if (r == null) return '—';
  const p = r * 100;
  if (p > 0.03)  return '🔴 Extrém – longok túlsúlyban';
  if (p > 0.01)  return '🟡 Longok dominálnak';
  if (p < -0.03) return '🔵 Extrém – shortok túlsúlyban';
  if (p < -0.01) return '🟢 Shortok dominálnak';
  return '⚪ Semleges';
}

function oiSignal(oiChange, priceChange) {
  if (oiChange == null || priceChange == null) return { text: '— Nincs adat', color: '#888888' };
  const oiUp = oiChange > 0.3, oiDn = oiChange < -0.3, prUp = priceChange > 0;
  if (oiUp &&  prUp) return { text: '🟢 Bullish folytatás',        color: '#00C853' };
  if (oiUp && !prUp) return { text: '🔴 Bearish folytatás',        color: '#FF4D4D' };
  if (oiDn &&  prUp) return { text: '🟠 Short Squeeze lehetséges', color: '#FF8C00' };
  if (oiDn && !prUp) return { text: '🟡 Long Squeeze lehetséges',  color: '#FFD700' };
  return { text: '— Semleges', color: '#888888' };
}

function lsSignal(ratio) {
  if (ratio == null) return '—';
  if (ratio > 2.0) return '🚨 Squeeze veszély';
  if (ratio > 1.5) return '⚠ Túl sok long';
  if (ratio > 1.2) return '📈 Long dominancia';
  if (ratio < 0.8) return '⚠ Túl sok short';
  if (ratio < 1.0) return '📉 Short dominancia';
  return '⚖ Kiegyensúlyozott';
}

function lsSignalColor(ratio) {
  if (ratio == null) return 'var(--text3)';
  if (ratio > 2.0) return '#ff4d4d';
  if (ratio > 1.5 || ratio < 0.8) return '#f5a623';
  if (ratio > 1.2) return '#00c853';
  if (ratio < 1.0) return '#ff8a80';
  return 'var(--text2)';
}

function sentimentLabel(score) {
  if (score <= 30) return { text: '🔴 Medve Hangulat', color: '#ff4d4d' };
  if (score <= 45) return { text: '🟡 Óvatos',         color: '#f5a623' };
  if (score <= 55) return { text: '⚪ Semleges',        color: 'var(--text2)' };
  if (score <= 70) return { text: '🟢 Optimista',       color: '#00c853' };
  return                   { text: '🚀 Eufória',         color: '#00e5ff' };
}

// ── Put/Call Section (self-contained) ────────────────────────────────────────

/* Called directly: Deribit's CORS allows our origin (the corsproxy.io detour
   answered 401 and was blocked by our CSP anyway). The ~450 KB answer is
   reduced to the totals before it is cached. */
const DERIBIT_URL = (ccy) =>
  `https://www.deribit.com/api/v2/public/get_book_summary_by_currency?currency=${ccy}&kind=option`;

function summarizeOptions(json) {
  const out = { putOI: 0, callOI: 0, putVol: 0, callVol: 0, putOIUsd: 0, callOIUsd: 0, count: 0 };
  for (const inst of json?.result ?? []) {
    const name = inst.instrument_name ?? '';
    const isPut = name.endsWith('-P');
    if (!isPut && !name.endsWith('-C')) continue;
    const oi  = inst.open_interest ?? 0;           // contracts (1 contract = 1 coin)
    const vol = inst.volume ?? 0;                  // 24h volume, contracts
    const usd = oi * (inst.underlying_price ?? 0); // notional
    if (isPut) { out.putOI += oi; out.putVol += vol; out.putOIUsd += usd; }
    else       { out.callOI += oi; out.callVol += vol; out.callOIUsd += usd; }
    out.count++;
  }
  return out;
}
const PC_REFRESH = 10 * 60 * 1000;

function pcSignal(ratio) {
  if (ratio == null) return { text: '—', color: 'var(--text3)', desc: '' };
  /* Descriptive labels only: they state where the ratio is, not what to do. */
  if (ratio < 0.5)  return { text: 'Nagyon alacsony put/call arány',   color: '#00ff88', desc: 'Jóval több nyitott call, mint put.' };
  if (ratio < 0.8)  return { text: 'Alacsony put/call arány',          color: '#00c853', desc: 'Több nyitott call, mint put.' };
  if (ratio < 1.2)  return { text: 'Kiegyensúlyozott put/call arány',  color: 'var(--text2)', desc: 'A put és a call nagyjából egyensúlyban.' };
  return                   { text: 'Magas put/call arány',             color: '#ff4d4d', desc: 'Több nyitott put, mint call.' };
}

function fmtOI(n) {
  if (n == null || isNaN(n)) return '—';
  return fmtCompact(n);
}

function PutCallSection() {
  const [pcData,    setPcData]    = useState({ BTC: null, ETH: null });
  const [pcLoading, setPcLoading] = useState(true);
  const [pcError,   setPcError]   = useState(false);
  const [pcCached,  setPcCached]  = useState(false);
  const pcCacheRef = useRef({ ts: 0 });
  const lastGoodRef = useRef(null);
  const retriedRef  = useRef(false);

  const fetchPC = useCallback(async (isManual = false) => {
    if (!isManual && Date.now() - pcCacheRef.current.ts < PC_REFRESH) return;
    setPcError(false);

    async function fetchCcy(ccy) {
      try {
        const sum = await fetchWithCache(DERIBIT_URL(ccy), { cacheDuration: PC_REFRESH, transform: summarizeOptions });
        if (!sum || !sum.count) return null;
        return {
          ...sum,
          ratio:    sum.callOI  > 0 ? sum.putOI  / sum.callOI  : null,   // by open interest
          volRatio: sum.callVol > 0 ? sum.putVol / sum.callVol : null,   // by 24h volume
          meta: getFetchMeta(DERIBIT_URL(ccy)),
        };
      } catch (_) { return null; }
    }

    const [btc, eth] = await Promise.all([fetchCcy('BTC'), fetchCcy('ETH')]);

    if (!btc && !eth) {
      // Fetch failed — fall back to last known data if available, else error + one retry
      if (lastGoodRef.current) {
        setPcData(lastGoodRef.current);
        setPcCached(true);
      } else {
        setPcError(true);
        if (!retriedRef.current) {
          retriedRef.current = true;
          setTimeout(() => fetchPC(true), 3000);
        }
      }
      setPcLoading(false);
      return;
    }

    const data = { BTC: btc, ETH: eth };
    lastGoodRef.current = data;
    retriedRef.current = false;
    setPcData(data);
    setPcCached(false);
    pcCacheRef.current.ts = Date.now();
    setPcLoading(false);
  }, []);

  useEffect(() => { fetchPC(); }, [fetchPC]);
  useEffect(() => {
    const id = setInterval(fetchPC, PC_REFRESH);
    return () => clearInterval(id);
  }, [fetchPC]);

  const currencies = [
    { key: 'BTC', label: 'Bitcoin (BTC)', color: '#f7931a' },
    { key: 'ETH', label: 'Ethereum (ETH)', color: '#627eea' },
  ];

  return (
    <section className="deriv-section">
      <div className="deriv-section-header">
        <span className="deriv-section-title deriv-section-title--opt">OPCIÓS PIAC — PUT/CALL ARÁNY<SourceDot source="Deribit (BTC és ETH opciók)" meta={(m => m && (pcCached ? { ...m, stale: true } : m))(mergeMeta(pcData.BTC?.meta, pcData.ETH?.meta))} error={pcError && !pcData.BTC} /></span>
        <button className="deriv-refresh-btn" onClick={() => { pcCacheRef.current.ts = 0; fetchPC(true); }}>↻ Frissítés</button>
        <button className="deriv-retry-btn" onClick={() => { retriedRef.current = false; pcCacheRef.current.ts = 0; setPcLoading(true); fetchPC(true); }}>🔄 Újrapróbálás</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
        Az opciós piac hangulata — fedezés vagy spekuláció?
      </div>

      {pcLoading ? (
        <div className="pc-skel">
          {[0, 1].map(i => (
            <div key={i} className="pc-skel-card">
              <div className="pc-skel-bar" style={{ width: '50%', height: 14 }} />
              <div className="pc-skel-bar" style={{ width: '70%', height: 28 }} />
              <div className="pc-skel-bar" style={{ width: '40%' }} />
              <div className="pc-skel-bar" style={{ width: '90%' }} />
            </div>
          ))}
        </div>
      ) : pcError ? (
        <div className="deriv-explain-box" style={{ marginBottom: 14 }}>
          ⚠️ A Deribit adatforrás most nem válaszol. Próbáld újra pár perc múlva.
          <div style={{ marginTop: 10 }}>
            <button className="deriv-retry-btn" onClick={() => { retriedRef.current = false; pcCacheRef.current.ts = 0; setPcLoading(true); fetchPC(true); }}>🔄 Újrapróbálás</button>
          </div>
        </div>
      ) : (
        <>
          <div className="pc-cards">
            {currencies.map(({ key, label, color }) => {
              const d   = pcData[key];
              const sig = pcSignal(d?.ratio ?? null);
              return (
                <div key={key} className="pc-card" style={{ borderTopColor: color }}>
                  <div className="pc-card-title" style={{ color }}>{label}</div>
                  <div className="pc-ratio" style={{ color: sig.color }}>
                    {d?.ratio != null ? fmtNum(d.ratio, 3) : '—'}
                  </div>
                  <div className="pc-ratio-label">Put/Call arány · nyitott pozíciók (OI)</div>
                  <div className="pc-ratio-label" style={{ marginTop: 4 }}>
                    24h forgalom alapján: <strong style={{ color: 'var(--text)' }}>{d?.volRatio != null ? fmtNum(d.volRatio, 3) : '—'}</strong>
                  </div>
                  <div className="pc-signal" style={{ color: sig.color }}>{sig.text}</div>
                  {sig.desc && <div className="pc-signal-desc">{sig.desc}</div>}
                  <div className="pc-oi-row">
                    <div className="pc-oi-item">
                      <span className="pc-oi-label">Put OI</span>
                      <span className="pc-oi-val negative">{fmtOI(d?.putOIUsd)}</span>
                    </div>
                    <div className="pc-oi-item">
                      <span className="pc-oi-label">Call OI</span>
                      <span className="pc-oi-val positive">{fmtOI(d?.callOIUsd)}</span>
                    </div>
                  </div>
                  {d?.putOI != null && d?.callOI != null && d.putOI + d.callOI > 0 && (
                    <div className="pc-bar-wrap">
                      <div className="pc-bar-track">
                        <div className="pc-bar-put"  style={{ width: `${(d.putOI  / (d.putOI + d.callOI)) * 100}%` }} />
                        <div className="pc-bar-call" style={{ width: `${(d.callOI / (d.putOI + d.callOI)) * 100}%` }} />
                      </div>
                      <div className="pc-bar-labels">
                        <span className="negative">PUT {fmtNum((d.putOI / (d.putOI + d.callOI)) * 100, 0)}%</span>
                        <span className="positive">CALL {fmtNum((d.callOI / (d.putOI + d.callOI)) * 100, 0)}%</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="deriv-explain-box" style={{ marginTop: 14 }}>
            <strong>📚 Put/Call Arány magyarázata:</strong><br />
            • <strong>PUT opció</strong> = védelmi hedge lefelé (bearish fogadás)<br />
            • <strong>CALL opció</strong> = felfelé irányuló spekuláció (bullish fogadás)<br />
            • Magas Put/Call (&gt;1.0) = sok intézmény hedge-el = félelem a piacon<br />
            • Alacsony Put/Call (&lt;0.7) = sok CALL vásárlás = bullish hangulat<br />
            • Nagyon alacsony (&lt;0.5) = szokatlanul sok nyitott call pozíció<br />
            • Az OI-alapú arány a nyitott opciós kontraktusok, a forgalmi arány az elmúlt 24 óra kötéseinek darabszámából számol.
          </div>
        </>
      )}
    </section>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SentimentGauge({ score }) {
  const lbl = sentimentLabel(score);
  const clamp = Math.max(0, Math.min(100, score));
  return (
    <div className="deriv-gauge-wrap">
      <div className="deriv-gauge-track">
        <div className="deriv-gauge-zones">
          <div style={{ flex: 30, background: 'rgba(255,77,77,0.35)',  borderRadius: '4px 0 0 4px' }} />
          <div style={{ flex: 15, background: 'rgba(245,166,35,0.35)' }} />
          <div style={{ flex: 10, background: 'rgba(136,136,136,0.25)' }} />
          <div style={{ flex: 15, background: 'rgba(0,200,83,0.35)' }} />
          <div style={{ flex: 30, background: 'rgba(0,229,255,0.35)',  borderRadius: '0 4px 4px 0' }} />
        </div>
        <div className="deriv-gauge-thumb" style={{ left: `${clamp}%` }} />
      </div>
      <div className="deriv-gauge-labels">
        <span>Medve</span><span>Óvatos</span><span>Semleges</span>
        <span>Optimista</span><span>Eufória</span>
      </div>
      <div className="deriv-gauge-score" style={{ color: lbl.color }}>
        {lbl.text} <span className="deriv-gauge-num">({fmtNum(clamp, 0)}/100)</span>
      </div>
    </div>
  );
}

function LsBar({ buyRatio, sellRatio }) {
  const longPct  = (buyRatio  * 100).toFixed(1);
  const shortPct = (sellRatio * 100).toFixed(1);
  return (
    <div className="deriv-ls-bar-wrap">
      <div className="deriv-ls-bar-track">
        <div className="deriv-ls-bar-long"  style={{ width: longPct  + '%' }} />
        <div className="deriv-ls-bar-short" style={{ width: shortPct + '%' }} />
      </div>
      <div className="deriv-ls-bar-labels">
        <span className="positive">Long {longPct}%</span>
        <span className="negative">Short {shortPct}%</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// FREE: one line under the BTC/ETH rows instead of the hidden coins / Put-Call.
/* FREE: a további 20 coin a Pluszban nyílik, a Put/Call a PRO-ban.
   Plusz: csak a Put/Call hiányzik, az a PRO-ban nyílik. */
function ProRow({ user, access, putCallOnly = false }) {
  const plusz = !putCallOnly;
  return (
    <div className="pro-row">
      <span>{plusz ? 'További 20 coin a Pluszban, a Put/Call a PRO-ban.' : 'A Put/Call-adatok a PRO-ban.'}</span>
      <button
        className="pro-row-cta"
        onClick={() => openLanding(plusz ? PLUSZ_PATH : '/pro', {
          user,
          event: plusz ? 'plusz_cta_click' : 'pro_cta_clicked',
          eventProps: plusz ? { hely: 'terminal_derivativak' } : { source: 'derivatives_row', tier: access?.tier || null },
        })}
      >
        {plusz ? 'Plusszal nyílik →' : 'PRO-val nyílik →'}
      </button>
    </div>
  );
}

export default function DerivativesTab({ fearGreedData, isFree = false, putCall = !isFree, user, access }) {
  // FREE: BTC and ETH only (reverse trial); fewer requests too.
  const COINS = isFree ? ALL_COINS.filter(c => FREE_SYMS.has(c.sym)) : ALL_COINS;
  const coinsKey = COINS.map(c => c.sym).join(',');
  const coinsRef = useRef(COINS);
  coinsRef.current = COINS;

  const [tickers,     setTickers]     = useState({});
  const [oiHist,      setOiHist]      = useState({});
  const [lsRatios,    setLsRatios]    = useState({});
  const [binFunding,  setBinFunding]  = useState({});
  const [loading,     setLoading]     = useState(true);
  const [bybitDown,   setBybitDown]   = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [liqSym,      setLiqSym]      = useState('BTCUSDT');

  const [oiSearch,   setOiSearch]   = useState('');
  const [oiExpanded, setOiExpanded] = useState(false);
  const [fundFilter, setFundFilter] = useState('all');

  const cacheRef = useRef({ ts: 0 });

  const fetchAll = useCallback(async (force = false) => {
    if (!force && Date.now() - cacheRef.current.ts < CACHE_TTL) return;

    let loaded = 0;
    setLoadedCount(0);
    try {

    // Bybit: ONE request for all linear tickers (price, funding, OI value,
    // index/mark), filtered locally — instead of one request per coin.
    let tickerMap = {};
    let tickersOk = true;
    try { tickerMap = await fetchLinearTickers(CACHE_TTL); } catch (_) { tickersOk = false; }

    // OI history and long/short ratio exist only per symbol: batched,
    // at most 4 requests in flight.
    const [oiResults, lsResults] = await Promise.all([
      mapLimit(coinsRef.current, 4, ({ sym }) =>
        fetchWithCache(`${BYBIT}/market/open-interest?category=linear&symbol=${sym}&intervalTime=4h&limit=2`, { cacheDuration: 30 * 60 * 1000 })
          .then(j => { setLoadedCount(++loaded); return { sym, list: j.result?.list ?? [] }; })
      ),
      mapLimit(coinsRef.current, 4, ({ sym }) =>
        fetchWithCache(`${BYBIT}/market/account-ratio?category=linear&symbol=${sym}&period=1h&limit=1`, { cacheDuration: 30 * 60 * 1000 })
          .then(j => ({ sym, d: j.result?.list?.[0] ?? null }))
      ),
    ]);

    // Binance: single premiumIndex call covers all coins
    let binMap = {};
    try {
      const binData = await fetchWithCache(`${BINANCE}/premiumIndex`, { cacheDuration: 5 * 60 * 1000 });
      if (Array.isArray(binData)) {
        binData.forEach(item => { binMap[item.symbol] = parseFloat(item.lastFundingRate); });
      }
    } catch {}

    // Build state maps
    const newTickers = {};
    coinsRef.current.forEach(({ sym }) => { newTickers[sym] = tickerMap[sym] ?? null; });

    const newOI = {};
    oiResults.forEach(res => {
      if (res.status !== 'fulfilled') return;
      const { sym, list } = res.value;
      const curr = list[0] ? parseFloat(list[0].openInterest) : null;
      const prev = list[1] ? parseFloat(list[1].openInterest) : null;
      newOI[sym] = {
        curr, prev,
        change4h: curr && prev && prev > 0 ? ((curr - prev) / prev) * 100 : null,
      };
    });

    const newLS = {};
    lsResults.forEach(res => {
      if (res.status !== 'fulfilled') return;
      const { sym, d } = res.value;
      if (!d) { newLS[sym] = null; return; }
      const buy  = parseFloat(d.buyRatio);
      const sell = parseFloat(d.sellRatio);
      newLS[sym] = { buy, sell, ratio: sell > 0 ? buy / sell : null };
    });

    // Bybit counts as down if the ticker list failed or no per-coin call answered.
    const anyPerCoin = [...oiResults, ...lsResults].some(r => r.status === 'fulfilled');
    setBybitDown(!tickersOk || !anyPerCoin);
    setTickers(newTickers);
    setOiHist(newOI);
    setLsRatios(newLS);
    setBinFunding(binMap);
    cacheRef.current.ts = Date.now();

    } catch (err) {
      console.error('DerivativesTab betöltési hiba:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(true); }, [fetchAll, coinsKey]);
  useEffect(() => {
    const id = setInterval(() => fetchAll(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const oiRows = COINS.map(({ sym, label }) => {
    const t  = tickers[sym];
    const oi = oiHist[sym];
    return {
      sym, label,
      oiUSD:    t  ? parseFloat(t.openInterestValue) : null,
      price:    t  ? parseFloat(t.lastPrice)          : null,
      change4h: oi?.change4h ?? null,
      priceChg: t  ? parseFloat(t.price24hPcnt) * 100 : null,
    };
  }).sort((a, b) => (b.oiUSD ?? 0) - (a.oiUSD ?? 0));

  const oiFiltered = oiSearch.trim()
    ? oiRows.filter(r => r.label.toLowerCase().includes(oiSearch.trim().toLowerCase()))
    : oiRows;

  const oiVisible = (oiExpanded || oiSearch.trim()) ? oiFiltered : oiFiltered.slice(0, 5);

  const lsRows = COINS.map(({ sym, label }) => ({
    sym, label, ls: lsRatios[sym] ?? null,
  })).sort((a, b) => (b.ls?.ratio ?? 0) - (a.ls?.ratio ?? 0));

  const squeezeCandidates = lsRows.filter(r => r.ls?.ratio != null && r.ls.ratio > 2.0);

  const fundRows = COINS.map(({ sym, label }) => {
    const t        = tickers[sym];
    const bybitRate = t ? parseFloat(t.fundingRate) : null;
    const binRate   = binFunding[sym] != null ? binFunding[sym] : null;
    const avgRate   = bybitRate != null && binRate != null
      ? (bybitRate + binRate) / 2
      : bybitRate ?? binRate;
    return { sym, label, bybitRate, binRate, avgRate };
  }).sort((a, b) => Math.abs(b.avgRate ?? 0) - Math.abs(a.avgRate ?? 0));

  const fundFiltered = fundRows.filter(r => {
    const v = r.avgRate ?? 0;
    if (fundFilter === 'longs')   return v > 0;
    if (fundFilter === 'shorts')  return v < 0;
    if (fundFilter === 'extreme') return Math.abs(v) > 0.0001;
    return true;
  });

  const btcOIUSD = tickers['BTCUSDT'] ? parseFloat(tickers['BTCUSDT'].openInterestValue) : null;

  const sentimentScore = (() => {
    const fgScore = fearGreedData?.[0] ? parseFloat(fearGreedData[0].value) : 50;
    const btcLS   = lsRatios['BTCUSDT'];
    const lsScore = btcLS?.ratio != null
      ? Math.max(0, Math.min(100, 50 + (btcLS.ratio - 1) * 50)) : 50;
    const rateVals = COINS
      .map(c => tickers[c.sym]?.fundingRate).filter(r => r != null).map(r => parseFloat(r));
    const avgRate  = rateVals.length ? rateVals.reduce((s, v) => s + v, 0) / rateVals.length : 0;
    const rateScore = Math.max(0, Math.min(100, 50 + avgRate * 100 * 500));
    const oiChanges = COINS.map(c => oiHist[c.sym]?.change4h).filter(v => v != null);
    const avgOI    = oiChanges.length ? oiChanges.reduce((s, v) => s + v, 0) / oiChanges.length : 0;
    const oiScore  = Math.max(0, Math.min(100, 50 + avgOI * 5));
    return fgScore * 0.30 + lsScore * 0.25 + rateScore * 0.25 + oiScore * 0.20;
  })();

  const liqPrice = tickers[liqSym] ? parseFloat(tickers[liqSym].lastPrice) : null;

  const tickMeta = tickersMeta();
  const oiMeta   = mergeMeta(...COINS.map(c => getFetchMeta(`${BYBIT}/market/open-interest?category=linear&symbol=${c.sym}&intervalTime=4h&limit=2`)));
  const lsMeta   = mergeMeta(...COINS.map(c => getFetchMeta(`${BYBIT}/market/account-ratio?category=linear&symbol=${c.sym}&period=1h&limit=1`)));
  const binMeta  = getFetchMeta(`${BINANCE}/premiumIndex`);

  const premiumRows = PREMIUM_SYMS.filter(sym => !isFree || FREE_SYMS.has(sym)).map(sym => {
    const label  = COINS.find(c => c.sym === sym)?.label ?? sym.replace('USDT', '');
    const t      = tickers[sym];
    const indexP = t ? parseFloat(t.indexPrice) : null;
    const markP  = t ? parseFloat(t.markPrice)  : null;
    const prem   = indexP && indexP > 0 ? ((markP - indexP) / indexP) * 100 : null;
    return { sym, label, indexPrice: indexP, markPrice: markP, premium: prem };
  }).sort((a, b) => (b.premium ?? 0) - (a.premium ?? 0));

  const contangoCount = premiumRows.filter(r => r.premium != null && r.premium > 0).length;
  const backwCount    = premiumRows.filter(r => r.premium != null && r.premium < 0).length;

  // ── Loading screen ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="deriv-loading">
        <div className="deriv-loading-icon">◈</div>
        <div>Derivatíva adatok betöltése…</div>
        <div className="deriv-loading-count">
          {loadedCount} / {COINS.length} coin betöltve
        </div>
        <div className="deriv-loading-bar-track">
          <div
            className="deriv-loading-bar-fill"
            style={{ width: `${(loadedCount / COINS.length) * 100}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="deriv-tab">
      {bybitDown && <DataSourceDown onRetry={() => fetchAll(true)} />}

      {/* ── Section 1: Open Interest ── */}
      <section className="deriv-section">
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--oi">OPEN INTEREST KÖVETŐ<SourceDot source="Bybit (open interest, ár)" meta={mergeMeta(tickMeta, oiMeta)} /></span>
          <span className="deriv-coin-badge">{COINS.length} coin</span>
          <button className="deriv-refresh-btn" onClick={() => fetchAll(true)}>↻ Frissítés</button>
        </div>

        {btcOIUSD != null && (
          <div className="deriv-summary-card">
            <span className="deriv-summary-label">BTC Open Interest</span>
            <span className="deriv-summary-val">{fmtUSD(btcOIUSD)}</span>
          </div>
        )}

        <input
          className="deriv-search"
          placeholder="Keresés..."
          value={oiSearch}
          onChange={e => setOiSearch(e.target.value)}
        />

        <div className="deriv-table-wrap">
          <table className="deriv-table">
            <thead>
              <tr>
                <th className="center">#</th>
                <th>Coin</th>
                <th className="right">OI (USD)</th>
                <th className="right">OI Változás 4h</th>
                <th className="right">Trend</th>
                <th className="right">Ár</th>
                <th>Szignál</th>
              </tr>
            </thead>
            <tbody>
              {oiVisible.map((row, idx) => {
                const sig = oiSignal(row.change4h, row.priceChg);
                return (
                  <tr key={row.sym} className={`deriv-tr ${row.change4h == null ? '' : row.change4h >= 0 ? 'deriv-tr-up' : 'deriv-tr-down'}`}>
                    <td className="center deriv-rank">{idx + 1}</td>
                    <td>
                      <span className="deriv-sym">{row.label}</span>
                      <button type="button" className="alert-row-btn" title="OI-riasztás"
                        aria-label={`OI-riasztás: ${row.label}`}
                        onClick={() => openAlertDraft({ kind: 'oi', symbol: row.sym, source: 'derivatives' })}>🔔</button>
                    </td>
                    <td className="right">{fmtUSD(row.oiUSD)}</td>
                    <td className={`right ${row.change4h == null ? '' : row.change4h >= 0 ? 'positive' : 'negative'}`}>
                      {row.change4h != null ? `${fmtPct(row.change4h, 2)}` : '—'}
                    </td>
                    <td className="right">
                      {row.change4h != null
                        ? <span style={{ color: row.change4h >= 0 ? '#00c853' : '#ff4d4d' }}>
                            {row.change4h >= 0 ? '▲' : '▼'}
                          </span>
                        : '—'}
                    </td>
                    <td className="right">{row.price != null ? fmtPrice(row.price) : '—'}</td>
                    <td><span className="deriv-oi-badge" style={{ color: sig.color, background: `${sig.color}22`, borderColor: `${sig.color}55` }}>{sig.text}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!oiSearch.trim() && oiRows.length > 5 && (
          <button className="deriv-expand-btn" onClick={() => setOiExpanded(e => !e)}>
            {oiExpanded
              ? '▲ Kevesebb mutatása'
              : `▼ Összes mutatása (${oiRows.length} coin)`}
          </button>
        )}
        {isFree && <ProRow user={user} access={access} />}
      </section>

      {/* ── Section 2: Long/Short Ratio ── */}
      <section className="deriv-section">
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--ls">LONG/SHORT ARÁNY <InfoTooltip text="Megmutatja, hogy a kereskedők mekkora része fogad az ár emelkedésére (long) vagy esésére (short). Ha nagyon sokan longolnak (pl. 80%+), az paradox módon veszélyes — ha az ár esni kezd, ezek a pozíciók mind egyszerre zárnak le, ami tovább gyorsítja az esést." /><SourceDot source="Bybit (long/short számlaarány, 1h)" meta={lsMeta} /></span>
          <span className="deriv-updated">{COINS.length} coin · 1h periódus · L/S csökkenő sorrendben</span>
        </div>

        {squeezeCandidates.length > 0 && (
          <div className="deriv-squeeze-warn">
            <div className="deriv-squeeze-title">⚠️ Long squeeze kockázat — L/S arány &gt; 2.0 (sok long) <InfoTooltip text="Squeeze = kiszorítás. Short squeeze: sokan fogadtak az esésre, de az ár hirtelen emelkedni kezd — mindenki pánikba esik és zárja a shortját, ami még jobban feltolja az árat. Long squeeze fordítva működik." /></div>
            <div className="deriv-squeeze-chips">
              {squeezeCandidates.map(r => (
                <span key={r.sym} className="deriv-squeeze-chip">
                  {r.label} <strong>{fmtNum(r.ls.ratio, 2)}×</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="deriv-ls-grid deriv-ls-grid--wide">
          {lsRows.filter(({ ls }) => ls && ls.ratio != null && ls.ratio > 0).map(({ sym, label, ls }) => {
            return (
              <div key={sym} className={`deriv-ls-card ${ls.ratio > 2.0 ? 'deriv-ls-card--danger' : ''}`}>
                <div className="deriv-ls-top">
                  <span className="deriv-ls-sym">{label}</span>
                  <span className="deriv-ls-ratio" style={{ color: lsSignalColor(ls.ratio) }}>
                    {ls.ratio != null ? fmtNum(ls.ratio, 2) : '—'}×
                  </span>
                </div>
                <LsBar buyRatio={ls.buy} sellRatio={ls.sell} />
                <div className="deriv-ls-signal" style={{ color: lsSignalColor(ls.ratio) }}>
                  {lsSignal(ls.ratio)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section 3: Funding Rate Table ── */}
      <section className="deriv-section">
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--fund">FUNDING RÁTA ÖSSZEFOGLALÓ<SourceDot source="Bybit · Binance" meta={mergeMeta(tickMeta, binMeta)} /></span>
          <span className="deriv-updated">8h periódus · |ráta| szerint rendezve</span>
        </div>

        <div className="deriv-fund-filters">
          {[
            { key: 'all',     label: 'Mind' },
            { key: 'longs',   label: 'Longok fizetnek' },
            { key: 'shorts',  label: 'Shortok fizetnek' },
            { key: 'extreme', label: '⚡ Extrém (>0,01%)' },
          ].map(f => (
            <button
              key={f.key}
              className={`deriv-filter-btn ${fundFilter === f.key ? 'active' : ''}`}
              onClick={() => setFundFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="deriv-table-wrap">
          <table className="deriv-table">
            <thead>
              <tr>
                <th>Coin</th>
                <th className="right">Bybit Funding</th>
                <th className="right">Binance Funding</th>
                <th className="right">Átlag</th>
                <th className="right">APR (becsült)</th>
                <th>Szignál</th>
              </tr>
            </thead>
            <tbody>
              {fundFiltered.map(({ sym, label, bybitRate, binRate, avgRate }) => (
                <tr key={sym} className="deriv-tr">
                  <td><span className="deriv-sym">{label}</span></td>
                  <td className="right" style={{ color: rateColor(bybitRate), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtRate(bybitRate)}
                  </td>
                  <td className="right" style={{ color: rateColor(binRate), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtRate(binRate)}
                  </td>
                  <td className="right" style={{ color: rateColor(avgRate), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtRate(avgRate)}
                  </td>
                  <td className="right" style={{ color: rateColor(avgRate), fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtAPR(avgRate)}
                  </td>
                  <td style={{ fontSize: 11 }}>{fundSignal(avgRate)}</td>
                </tr>
              ))}
              {fundFiltered.length === 0 && (
                <tr>
                  <td colSpan={6} className="deriv-empty-row">Nincs a szűrőnek megfelelő coin</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Section 4: Sentiment Gauge ── */}
      <section className="deriv-section deriv-sentiment-section" style={{
        boxShadow: sentimentScore >= 60
          ? 'inset 0 0 0 1px rgba(0,255,136,0.3), 0 0 20px -4px rgba(0,255,136,0.3)'
          : sentimentScore >= 40
          ? 'inset 0 0 0 1px rgba(255,200,0,0.2), 0 0 20px -4px rgba(255,200,0,0.2)'
          : 'inset 0 0 0 1px rgba(255,60,60,0.2), 0 0 20px -4px rgba(255,60,60,0.2)',
      }}>
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--sent">PIACI HANGULAT ÖSSZESÍTŐ <InfoTooltip text="Több forrás (Fear & Greed index, long/short arány, funding ráta, OI) alapján mutatja, mennyire optimista vagy pesszimista a piac. Extrém félelem általában jó vételi lehetőség szokott lenni — mások akkor adnak el, amikor kellene venni." /><SourceDot source="Alternative.me Fear & Greed · Bybit" meta={mergeMeta(tickMeta, lsMeta, oiMeta)} /></span>
        </div>
        <div className="deriv-sentiment-wrap">
          <SentimentGauge score={sentimentScore} />
          <div className="deriv-sentiment-breakdown">
            <div className="deriv-breakdown-row">
              <span className="deriv-breakdown-label"><span className="deriv-bd-dot" style={{ background: '#f5a623' }} />Félelem &amp; Mohóság (30%)</span>
              <span className="deriv-breakdown-val">
                {fearGreedData?.[0] ? `${fearGreedData[0].value} — ${fngLabel(fearGreedData[0].value_classification)}` : '—'}
              </span>
            </div>
            <div className="deriv-breakdown-row">
              <span className="deriv-breakdown-label"><span className="deriv-bd-dot" style={{ background: '#00D4FF' }} />BTC Long/Short (25%)</span>
              <span className="deriv-breakdown-val">
                {lsRatios['BTCUSDT']?.ratio != null ? fmtNum(lsRatios['BTCUSDT'].ratio, 3) : '—'}
              </span>
            </div>
            <div className="deriv-breakdown-row">
              <span className="deriv-breakdown-label"><span className="deriv-bd-dot" style={{ background: '#8C50FF' }} />Átl. Funding Ráta (25%)</span>
              <span className="deriv-breakdown-val">
                {(() => {
                  const vals = COINS
                    .map(c => tickers[c.sym]?.fundingRate)
                    .filter(r => r != null).map(r => parseFloat(r));
                  if (!vals.length) return '—';
                  return fmtRate(vals.reduce((s, v) => s + v, 0) / vals.length);
                })()}
              </span>
            </div>
            <div className="deriv-breakdown-row">
              <span className="deriv-breakdown-label"><span className="deriv-bd-dot" style={{ background: '#00ff88' }} />OI Trend (20%) <InfoTooltip text="Az összes nyitott futures pozíció összértéke. Ha az OI nő és az ár is emelkedik — erős, meggyőző trend. Ha az OI nő de az ár esik — egyre több short pozíció nyílik. Ha az OI csökken, pozíciókat zárnak — a trend gyengülhet." /></span>
              <span className="deriv-breakdown-val">
                {(() => {
                  const vals = COINS.map(c => oiHist[c.sym]?.change4h).filter(v => v != null);
                  if (!vals.length) return '—';
                  const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
                  return `${fmtPct(avg, 2)}`;
                })()}
              </span>
            </div>
          </div>
        </div>
        <SourceAttr alt />
      </section>

      {/* ── Section 5: Liquidation Levels ── */}
      <section className="deriv-section">
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--liq">LIKVIDÁCIÓS SZINTEK BECSLÉSE<SourceDot source="Bybit (aktuális ár)" meta={tickMeta} /></span>
          <select
            className="deriv-liq-select"
            value={liqSym}
            onChange={e => setLiqSym(e.target.value)}
          >
            {COINS.map(c => (
              <option key={c.sym} value={c.sym}>{c.label}</option>
            ))}
          </select>
        </div>

        {liqPrice ? (
          <>
            <div className="deriv-liq-current">
              Jelenlegi ár: <strong>{fmtPrice(liqPrice)}</strong>
            </div>
            <div className="deriv-table-wrap">
              <table className="deriv-table">
                <thead>
                  <tr>
                    <th>Tőkeáttétel</th>
                    <th className="right">Long Likvidációs Ár</th>
                    <th className="right">Short Likvidációs Ár</th>
                    <th>Kockázat szint</th>
                  </tr>
                </thead>
                <tbody>
                  {LIQ_LEVERAGES.map(({ lev, labelRisk }) => {
                    const longLiq  = liqPrice * (1 - 1 / lev);
                    const shortLiq = liqPrice * (1 + 1 / lev);
                    const riskColor = lev <= 2  ? 'var(--text2)'
                      : lev <= 5  ? '#f5a623'
                      : lev <= 10 ? '#ff8a80'
                      : '#ff4d4d';
                    return (
                      <tr key={lev} className="deriv-tr">
                        <td><span className="deriv-lev-badge">{lev}×</span></td>
                        <td className="right negative">{fmtPrice(longLiq)}</td>
                        <td className="right positive">{fmtPrice(shortLiq)}</td>
                        <td><span style={{ color: riskColor, fontSize: 11, fontWeight: 600 }}>{labelRisk}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="deriv-liq-note">
              ⚠ Becslés — tényleges likvidáció tőzsdei díjaktól és funding rátától is függ.
            </div>
          </>
        ) : (
          <div className="deriv-na" style={{ padding: 16 }}>Nincs áradat</div>
        )}
      </section>

      {/* ── Section 6: Futures Premium Index ── */}
      <section className="deriv-section">
        <div className="deriv-section-header">
          <span className="deriv-section-title deriv-section-title--prem">FUTURES PRÉMIUM INDEX<SourceDot source="Bybit (index- és mark ár)" meta={tickMeta} /></span>
        </div>
        <div className="deriv-depth-sub" style={{ marginBottom: 12 }}>
          Pozitív = Contango (bullish), negatív = Backwardation (bearish)
        </div>
        <div className="deriv-taker-summary" style={{ marginBottom: 10 }}>
          <span style={{ color: '#00c853' }}>▲ {contangoCount} Contango</span>
          {' · '}
          <span style={{ color: '#ff4d4d' }}>▼ {backwCount} Backwardation</span>
        </div>
        <div className="deriv-table-wrap">
          <table className="deriv-table">
            <thead>
              <tr>
                <th>Coin</th>
                <th className="right">Index Ár</th>
                <th className="right">Mark Ár</th>
                <th className="right">Prémium %</th>
                <th>Állapot</th>
                <th>Leírás</th>
              </tr>
            </thead>
            <tbody>
              {premiumRows.map(r => {
                const state = premiumState(r.premium);
                return (
                  <tr key={r.sym} className="deriv-tr">
                    <td><span className="deriv-sym">{r.label}</span></td>
                    <td className="right">{r.indexPrice != null ? fmtPrice(r.indexPrice) : '—'}</td>
                    <td className="right">{r.markPrice  != null ? fmtPrice(r.markPrice)  : '—'}</td>
                    <td className="right">
                      <span style={{
                        color: r.premium == null ? 'var(--text3)'
                          : r.premium > 0 ? '#00c853' : '#ff4d4d',
                        fontWeight: 700
                      }}>
                        {r.premium != null ? `${fmtPct(r.premium, 4)}` : '—'}
                      </span>
                    </td>
                    <td>
                      <span className="deriv-prem-badge" style={{ background: `${state.color}22`, color: state.color, borderColor: `${state.color}55` }}>
                        {state.text}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text3)' }}>{state.desc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="deriv-explain-box" style={{ marginTop: 12 }}>
          💡 <strong>Contango:</strong> futures &gt; spot → piac további áremelkedést vár · <strong>Backwardation:</strong> futures &lt; spot → eladási nyomás dominál
        </div>
      </section>

      {/* ── Section 7: Put/Call Ratio ── */}
      {isFree ? <ProRow user={user} access={access} /> : putCall ? <PutCallSection /> : <ProRow user={user} access={access} putCallOnly />}
    </div>
  );
}
